import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

/**
 * Theme wrapper around the Engrain Sightmap embed
 * (`custom_engrain_sightmap_url`). Drop-shadow, height cap, and the
 * click-to-interact overlay live here so the block embed stays untouched.
 * The iframe would otherwise capture every scroll and touch that lands on
 * it. After the first click, the overlay stays off for the rest of the visit.
 */
export function InteractiveMapSection() {
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: 'engrain-sitemap',
  });
  const [isInteractive, setIsInteractive] = useState(false);
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="ims-section" style={ctaColorVars}>
      <div className="ims-shadow">
        <div className="ims-frame">
          <EditableToken
            path="interactivemapsection.map_block"
            as="div"
            className="ims-block"
          >
            {mapSlot ? (
              <SectionContextProvider sectionId={mapSlot.sectionId}>
                <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="ims-empty">No block selected</div>
            )}
          </EditableToken>
          {mapSlot && !isInteractive ? (
            <button
              type="button"
              className="ims-overlay"
              onClick={() => setIsInteractive(true)}
              aria-label="Click to interact with the map"
            >
              <span className="ims-overlay-cta">Click to interact</span>
            </button>
          ) : null}
        </div>
      </div>

      <style>{`
        .ims-section {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          box-sizing: border-box;
          background: var(--vibe-color-background, #ffffff);
          padding: 2.5rem 1.25rem;
          --ims-map-height: min(78vh, 720px);
        }
        @media (min-width: 768px) {
          .ims-section {
            padding: 3rem 1.5rem;
            --ims-map-height: min(82vh, 900px);
          }
        }
        @media (min-width: 1200px) {
          .ims-section { --ims-map-height: min(88vh, 980px); }
        }

        /* Shadow sits on the wrapper, not the block, so it is visible on
           all four sides of a full-bleed embed without clipping the iframe. */
        .ims-shadow {
          max-width: min(80rem, 100%);
          min-width: 0;
          margin: 0 auto;
          box-shadow:
            0 -14px 40px rgba(0, 0, 0, 0.12),
            0 22px 56px rgba(0, 0, 0, 0.18),
            0 6px 18px rgba(0, 0, 0, 0.08);
        }

        .ims-frame {
          position: relative;
          height: var(--ims-map-height);
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
          overscroll-behavior-x: none;
          touch-action: pan-y;
          background: #ffffff;
        }

        .ims-block,
        .ims-block :is(section, iframe, .rrac_interactive_leasing_view) {
          display: block;
          box-sizing: border-box;
          width: 100%;
          max-width: 100% !important;
          min-width: 0 !important;
          height: 100% !important;
          max-height: 100% !important;
          min-height: 0 !important;
        }

        .ims-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }

        /* Section-owned overlay. Sits on the frame, never on the block, so
           the embed keeps its own markup and the iframe cannot steal scroll
           until the visitor opts in. */
        .ims-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          touch-action: pan-y;
        }
        .ims-overlay:hover,
        .ims-overlay:focus-visible {
          background: rgba(0, 0, 0, 0.18);
        }
        .ims-overlay:focus-visible {
          outline: 2px solid var(--cta-bg, var(--vibe-color-secondary));
          outline-offset: -2px;
        }
        /* Same solid CTA as Hero / inner-page heroes: heading face, color-4
           fill, 1.5px border, invert to white on hover. */
        .ims-overlay-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 400;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--cta-text, #fff);
          background: var(--cta-bg, var(--vibe-color-secondary));
          border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
          border-radius: 2px;
          padding: 1rem 2.5rem;
          box-shadow: none;
          transition: background-color 0.25s ease, color 0.25s ease,
                      border-color 0.25s ease, transform 0.25s ease;
        }
        .ims-overlay:hover .ims-overlay-cta,
        .ims-overlay:focus-visible .ims-overlay-cta {
          background: #ffffff;
          border-color: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          transform: translateY(-2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .ims-overlay-cta { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      defaultValue: 'engrain-sitemap',
      acceptedBlockCategories: ['Integrations', 'Embeds'],
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InteractiveMapSection;
